<template>
  <div v-if="chartData" class="calendar">
    <div class="header">
      <a id="previousyear" class="arrow" @click="movePreviousYear">&laquo;</a>
      <a id="previousmonth" class="arrow" @click="movePreviousMonth"
        >&lsaquo;</a
      >
      <span class="title" @click="moveThisMonth">{{ header.label }}</span>
      <a id="nextmonth" class="arrow" @click="moveNextMonth">&rsaquo;</a>
      <a id="nextyear" class="arrow" @click="moveNextYear">&raquo;</a>
    </div>
    <b-tooltip target="previousyear" triggers="hover">Previous Year</b-tooltip>
    <b-tooltip target="previousmonth" triggers="hover"
      >Previous Month</b-tooltip
    >
    <b-tooltip target="nextmonth" triggers="hover">Next Month</b-tooltip>
    <b-tooltip target="nextyear" triggers="hover">Next Year</b-tooltip>
    <div class="weekdays">
      <div class="weekday" v-for="(weekday, index) in weekdays" :key="index">
        {{ weekday.label_3 }}
      </div>
    </div>
    <div class="week" v-for="(week, index) in weeks" :key="index">
      <!-- "{ today: day.isToday, 'not-in-month': !day.inMonth }" -->
      <div
        class="day"
        :class="{
          today: day.isToday,
          'not-in-month': !day.inMonth,
          highlightday: day.inMonth && day.low,
          leadprice: day.leadprice,
          'highlightday leadprice': day.leadprice && day.low,
          selected: day.isSelected && !day.dragActive,
          dragged: day.isDragged,
        }"
        v-for="(day, index) in week"
        :key="index"
        @click="$emit('selectDay', day)"
        @mouseenter="$emit('enterDay', day)"
        @mouseleave="$emit('leaveDay', day)"
      >
        {{ day[`day`] }}
        <div v-if="day['value']">
          <b-badge
            :id="e.label"
            class="
              dayevent
              ellipsis
              mb-1
              d-sm-block d-inline-block
              w-sm-4
              h-sm-4
            "
            :variant="e.variant"
            v-for="(e, i) in day['value']"
            :title="e.label"
            :key="i"
            ><span class="d-none d-sm-table-cell">{{ e.label }}</span></b-badge
          >
          <!--   <span class="dayevent" v-for="(e,i) in day['value']" :key="i">{{e}}</span> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

// Calendar data
const _daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const _weekdayLabels = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
// const _weekdayLength = 3;
// const _weekdayCasing = "title";
const _monthLabels = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Today's data
const _today = new Date();
const _todayComps = {
  year: _today.getFullYear(),
  month: _today.getMonth() + 1,
  day: _today.getDate(),
};

export default {
  name: "Calendar-chart",
  props: ["data", "options", "refresh"],
  data() {
    return {
      month: this.$moment().month(),
      year: this.$moment().year(),
      defaultData: {
        date: "01/07/2020",
        selected: "05/07/2020",
        datasets: [
          {
            backgroundColor: [],
            data: [],
          },
        ],
        rows: [
          {
            from: "LBA",
            to: "IBZ",
            name1: "ryanair",
            name2: "ryanair",
            prices: [
              {
                date: "02/07/2020",
                price: 101.98,
              },
              {
                date: "05/07/2020",
                price: 87.98,
                class: "weekend",
              },
            ],
          },
          {
            from: "LPL",
            to: "IBZ",
            name1: "ryanair",
            name2: "ryanair",
            prices: [
              {
                date: "04/07/2020",
                price: 85.98,
                class: "weekend",
              },
              {
                date: "06/07/2020",
                price: 61.98,
                class: "cheapest",
              },
            ],
          },
          {
            from: "MAN",
            to: "IBZ",
            name1: "tui",
            name2: "ryanair",
            prices: [
              {
                date: "03/07/2020",
                price: 213.99,
              },
              {
                date: "06/07/2020",
                price: 235.99,
              },
            ],
          },
        ],
        total: 180,
      },
      defaultOptions: {
        responsive: true,
        maintainAspectRatio: true,
        datalabels: {
          display: false,
        },
        title: {
          display: true,
          text: "Calendar Chart",
        },
        legend: {
          display: false,
          position: "bottom",
        },
      },
      chartData: null,
      chartOptions: null,
    };
  },
  watch: {
    data: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal) {
          this.render();
        }
      },
    },
    options: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal) {
          this.render();
        }
      },
    },
  },
  computed: {
    // Our component exposes month as 1-based, but sometimes we need 0-based
    monthIndex() {
      return this.month - 1;
    },
    isLeapYear() {
      return (
        (this.year % 4 === 0 && this.year % 100 !== 0) || this.year % 400 === 0
      );
    },
    // Day/month/year components for previous month
    previousMonthComps() {
      if (this.month === 1)
        return {
          days: _daysInMonths[11],
          month: 12,
          year: this.year - 1,
        };
      return {
        days:
          this.month === 3 && this.isLeapYear
            ? 29
            : _daysInMonths[this.month - 2],
        month: this.month - 1,
        year: this.year,
      };
    },
    // Day/month/year components for next month
    nextMonthComps() {
      if (this.month === 12)
        return {
          days: _daysInMonths[0],
          month: 1,
          year: this.year + 1,
        };
      return {
        days:
          this.month === 2 && this.isLeapYear ? 29 : _daysInMonths[this.month],
        month: this.month + 1,
        year: this.year,
      };
    },
    // State for calendar header (no dependencies yet...)
    months() {
      return _monthLabels.map((ml, i) => ({
        label: ml,
        label_1: ml.substring(0, 1),
        label_2: ml.substring(0, 2),
        label_3: ml.substring(0, 3),
        number: i + 1,
      }));
    },
    // State for weekday header (no dependencies yet...)
    weekdays() {
      return _weekdayLabels.map((wl, i) => ({
        label: wl,
        label_1: wl.substring(0, 1),
        label_2: wl.substring(0, 2),
        label_3: wl.substring(0, 3),
        number: i + 1,
      }));
    },
    // State for calendar header
    header() {
      const month = this.months[this.monthIndex];
      return {
        month: month,
        year: this.year,
        shortYear: this.year.toString().substring(2, 4),
        label: month.label + " " + this.year,
      };
    },
    // Returns number for first weekday (1-7), starting from Sunday
    firstWeekdayInMonth() {
      let fwdim = new Date(this.year, this.monthIndex, 1).getDay();
      return fwdim == 0 ? 7 : fwdim;
    },
    // Returns number of days in the current month
    daysInMonth() {
      // Check for February in a leap year
      if (this.month === 2 && this.isLeapYear) return 29;
      // ...Just a normal month
      return _daysInMonths[this.monthIndex];
    },
    weeks() {
      const weeks = [];
      let previousMonth = true,
        thisMonth = false,
        nextMonth = false;
      let day = this.previousMonthComps.days - this.firstWeekdayInMonth + 2;
      let month = this.previousMonthComps.month;
      let year = this.previousMonthComps.year;
      // Cycle through each week of the month, up to 6 total
      for (let w = 1; w <= 6 && !nextMonth; w++) {
        // Cycle through each weekday
        const week = [];
        for (let d = 1; d <= 7; d++) {
          // We need to know when to start counting actual month days
          if (previousMonth && d >= this.firstWeekdayInMonth) {
            // Reset day/month/year counters
            day = 1;
            month = this.month;
            year = this.year;
            // ...and flag we're tracking actual month days
            previousMonth = false;
            thisMonth = true;
          }

          // Append day info for the current week
          // Note: this might or might not be an actual month day
          //  We don't know how the UI wants to display various days,
          //  so we'll supply all the data we can
          const dayInfo = {
            label: day && thisMonth ? day.toString() : "",
            day,
            price: null, //day==2 ? 150 :"" || day==6 ? 199 : "",
            low: null, //day==2 ? true : null,
            weekday: d,
            week: w,
            month,
            year,
            date: new Date(year, month - 1, day),
            beforeMonth: previousMonth,
            afterMonth: nextMonth,
            inMonth: thisMonth,
            isToday:
              day === _todayComps.day &&
              month === _todayComps.month &&
              year === _todayComps.year,
            isFirstDay: thisMonth && day === 1,
            isLastDay: thisMonth && day === this.daysInMonth,
          };

          if (this.chartData) {
            if (this.chartData.datasets) {
              let size = this.chartData.datasets[0].data.length;
              // // FIND THE LOWEST PRICE
              // let lowest = 100000;
              // for (var j = 0; j < size; j++) {
              //   if (Math.ceil(this.data[j].packageprice * 0.01) < lowest) {
              //     lowest = Math.ceil(this.data[j].packageprice * 0.01);
              //   }
              // }

              if (size > 0) {
                let items = this.chartData.datasets[0].data.filter(
                  (obj) =>
                    obj.date == this.$moment(dayInfo.date).format("YYYY-MM-DD")
                );
                if (items) {
                  dayInfo.value = [];
                  for (var j = 0; j < items.length; j++) {
                    dayInfo.value.push({
                      label: items[j].label,
                      variant: items[j].variant,
                    });
                  }
                }
              }
            }
          }

          //NOW SET THE DATES
          // for (var i = 0; i < size; i++) {
          //   let item = this.chartData.datasets[0].data[i];
          //   var d1 = this.$moment.utc(item.date, "DD/MM/YYYY").toDate();
          //   var d2 = dayInfo.date
          //   if (d1 == d2) {
          //     dayInfo.value = item.value;
          //     // if (dayInfo.price == lowest) {
          //     //   dayInfo.low = true;
          //     // }
          //   }
          // }

          // if (
          //   Date.parse(dayInfo.date).toString("yyyy/MM/dd") ==
          //   Date.parse(this.departuredate).toString("yyyy/MM/dd")
          // ) {
          //   dayInfo.leadprice = true;
          // }

          this.$emit("configureDay", dayInfo); // <=== THIS IS THE NEW EVENT WE RAISE NOW

          // AFTER EMIT
          if (this.chartData.selected) {
            if (
              this.$moment(dayInfo.date).format("DD/MM/YYYY") ==
              this.$moment(this.chartData.selected).format("DD/MM/YYYY")
            ) {
              dayInfo.isSelected = true;
            }
          }

          week.push(dayInfo);

          // We've hit the last day of the month
          if (thisMonth && day >= this.daysInMonth) {
            thisMonth = false;
            nextMonth = true;
            day = 1;
            month = this.nextMonthComps.month;
            year = this.nextMonthComps.year;
            // Still in the middle of the month (hasn't ended yet)
          } else {
            day++;
          }
        }
        // Append week info for the month
        weeks.push(week);
      }
      return weeks;
    },
  },
  methods: {
    async render() {
      console.log("Calendar-chart render");
      if (this.data) {
        this.chartData = this.data;
      } else {
        this.chartData = this.defaultData;
      }

      if (this.$moment(this.chartData.date, "DD/MM/YYYY", true).isValid()) {
        this.year = this.$moment(this.chartData.date, "DD/MM/YYYY")
          .toDate()
          .getFullYear();
        this.month =
          this.$moment(this.chartData.date, "DD/MM/YYYY").toDate().getMonth() +
          1;

        if (this.options) {
          this.chartOptions = this.options;
        } else {
          this.chartOptions = this.defaultOptions;
        }
      }
    },
    moveThisMonth() {
      this.month = _todayComps.month;
      this.year = _todayComps.year;
      this.$emit("changeMonth", this.year + "/" + ("0" + this.month).slice(-2));
    },
    moveNextMonth() {
      const { month, year } = this.nextMonthComps;
      this.month = month;
      this.year = year;
      this.$emit("changeMonth", this.year + "/" + ("0" + this.month).slice(-2));
    },
    movePreviousMonth() {
      const { month, year } = this.previousMonthComps;
      this.month = month;
      this.year = year;
      this.$emit("changeMonth", this.year + "/" + ("0" + this.month).slice(-2));
    },
    moveNextYear() {
      this.year++;
      this.$emit("changeMonth", this.year + "/" + ("0" + this.month).slice(-2));
    },
    movePreviousYear() {
      this.year--;
      this.$emit("changeMonth", this.year + "/" + ("0" + this.month).slice(-2));
    },
  },
};
</script>

<style>
</style>
